const normalizeSpeciesInputKey = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "_")
    .replace(/^_+|_+$/g, "");

const buildSpecies = ({ key, iconKey = key, labels, aliases = [] }) =>
  Object.freeze({
    key,
    iconKey,
    labels: Object.freeze(labels),
    aliases: Object.freeze(aliases),
  });

export const SPECIES_CATALOG_METADATA = Object.freeze([
  buildSpecies({
    key: "acai",
    labels: { en: "Açaí", pt: "Açaí", ja: "アサイー" },
    aliases: ["acai", "açaí"],
  }),
  buildSpecies({
    key: "banana",
    labels: { en: "Banana", pt: "Banana", ja: "バナナ" },
    aliases: ["banana"],
  }),
  buildSpecies({
    key: "blackPepper",
    iconKey: "black_papper",
    labels: { en: "Black pepper", pt: "Pimenta-do-reino", ja: "黒胡椒" },
    aliases: [
      "blackPepper",
      "black pepper",
      "black_pepper",
      "black_papper",
      "pimenta-do-reino",
      "pimenta do reino",
      "pimenta_do_reino",
      "Piper nigrum",
    ],
  }),
  buildSpecies({
    key: "brazilNuts",
    iconKey: "brazil_nuts",
    labels: { en: "Brazil nut", pt: "Castanha-do-Brasil", ja: "ブラジルナッツ" },
    aliases: [
      "brazilNuts",
      "brazil nuts",
      "brazil_nuts",
      "castanha-do-brasil",
      "castanha do brasil",
      "castanha_do_brasil",
      "Bertholletia excelsa",
    ],
  }),
  buildSpecies({
    key: "cassava",
    iconKey: "cacava",
    labels: { en: "Cassava", pt: "Mandioca", ja: "キャッサバ" },
    aliases: ["cassava", "manioc", "mandioca", "cacava", "caçava", "Manihot esculenta"],
  }),
  buildSpecies({
    key: "cocoa",
    labels: { en: "Cocoa", pt: "Cacau", ja: "カカオ" },
    aliases: ["cocoa", "cacao", "cacau", "Theobroma cacao"],
  }),
  buildSpecies({
    key: "coconut",
    labels: { en: "Coconut", pt: "Coco", ja: "ココナッツ" },
    aliases: ["coconut", "coco", "Cocos nucifera"],
  }),
  buildSpecies({
    key: "coffee",
    labels: { en: "Coffee", pt: "Café", ja: "コーヒー" },
    aliases: ["coffee", "cafe", "café", "Coffea arabica", "Coffea canephora"],
  }),
  buildSpecies({
    key: "cupuacu",
    labels: { en: "Cupuaçu", pt: "Cupuaçu", ja: "クプアス" },
    aliases: ["cupuacu", "cupuaçu", "Theobroma grandiflorum"],
  }),
  buildSpecies({
    key: "eucalyptus",
    iconKey: "wood",
    labels: { en: "Eucalyptus", pt: "Eucalipto", ja: "ユーカリ" },
    aliases: ["eucalyptus", "eucalipto", "wood", "Eucalyptus spp."],
  }),
  buildSpecies({
    key: "jackfruit",
    iconKey: "wood",
    labels: { en: "Jackfruit", pt: "Jaca", ja: "ジャックフルーツ" },
    aliases: ["jackfruit", "jaca", "Artocarpus heterophyllus"],
  }),
  buildSpecies({
    key: "lime",
    labels: { en: "Tahiti lime", pt: "Limão Tahiti", ja: "タヒチライム" },
    aliases: ["lime", "tahiti lime", "limao", "limão", "limao tahiti", "limão tahiti"],
  }),
  buildSpecies({
    key: "mango",
    labels: { en: "Mango", pt: "Manga", ja: "マンゴー" },
    aliases: ["mango", "manga", "Mangifera indica"],
  }),
  buildSpecies({
    key: "orange",
    labels: { en: "Orange", pt: "Laranja", ja: "オレンジ" },
    aliases: ["orange", "laranja", "Citrus sinensis"],
  }),
  buildSpecies({
    key: "papaya",
    labels: { en: "Papaya", pt: "Mamão", ja: "パパイヤ" },
    aliases: ["papaya", "mamao", "mamão", "Carica papaya"],
  }),
  buildSpecies({
    key: "pineapple",
    iconKey: "papaya",
    labels: { en: "Pineapple", pt: "Abacaxi", ja: "パイナップル" },
    aliases: ["pineapple", "abacaxi", "Ananas comosus"],
  }),
]);

const buildAliasMap = () => {
  const map = {};
  SPECIES_CATALOG_METADATA.forEach((species) => {
    [
      species.key,
      species.iconKey,
      species.labels.en,
      species.labels.pt,
      species.labels.ja,
      ...species.aliases,
    ].forEach((alias) => {
      const normalized = normalizeSpeciesInputKey(alias);
      if (normalized) {
        map[normalized] = species.key;
      }
    });
  });
  return Object.freeze(map);
};

export const SPECIES_KEY_ALIASES = buildAliasMap();

export const getSpeciesAliases = (species) => {
  if (!species) return [];
  const aliases = [
    species.key,
    species.iconKey,
    species.labels.en,
    species.labels.pt,
    species.labels.ja,
    ...species.aliases,
  ];
  const seen = new Set();
  return aliases.filter((alias) => {
    if (!alias || seen.has(alias)) return false;
    seen.add(alias);
    return true;
  });
};

export { normalizeSpeciesInputKey };
